import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown,
  faCheck,
  faCircleCheck,
  faShieldHalved,
  faCreditCard,
  faReceipt,
  faCalendarCheck,
  faInfinity,
  faGraduationCap,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import PremiumLayout from './PremiumLayout';
import PremiumSteps from './PremiumSteps';
import { formatRoDate } from './formatRoDate';
import { formatLeiExact } from './money';
import { useEnsureProfile } from './useEnsureProfile';

const apiUrl = process.env.REACT_APP_API_URL;

// Iconițe pentru cele trei beneficii din catalog (în ordinea în care vin de pe server).
const BENEFIT_ICONS = [faGraduationCap, faInfinity, faBolt];

const TrustBadges = () => (
  <div className="pm-trust">
    <span>
      <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" /> Plată securizată SSL
    </span>
    <span>
      <FontAwesomeIcon icon={faCreditCard} aria-hidden="true" /> Procesat de Stripe
    </span>
    <span>
      <FontAwesomeIcon icon={faReceipt} aria-hidden="true" /> Fără reînnoire automată
    </span>
  </div>
);

const BenefitsPanel = ({ plan, title }) => (
  <section className="pm-card pm-panel" aria-labelledby="pm-benefits-title">
    <h2 id="pm-benefits-title" className="pm-co-title">{title}</h2>
    <ul className="pm-features">
      {(plan?.benefits || []).map((benefit, index) => (
        <li key={benefit} className="pm-feature">
          <span className="pm-feature-icon" aria-hidden="true">
            <FontAwesomeIcon icon={BENEFIT_ICONS[index] || faCheck} />
          </span>
          <span className="pm-feature-text">{benefit}</span>
        </li>
      ))}
    </ul>
    <p className="pm-note pm-note--left">
      Plătești o singură dată pentru o lună de acces. Nu există reînnoire automată — poți prelungi oricând, iar
      perioada nouă se adaugă la cea rămasă.
    </p>
    <TrustBadges />
  </section>
);

const PremiumPricing = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth?.user;
  useEnsureProfile(auth);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiUrl}/premium/plans`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('plans'))))
      .then((data) => {
        if (!cancelled) setPlan(data.plans?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) setError('Oferta nu a putut fi încărcată. Încearcă din nou.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const price = plan ? formatLeiExact(plan.priceBani) : '—';

  if (user?.is_premium) {
    return (
      <PremiumLayout title="Esti deja Premium" subtitle="Ai acces complet la tot conținutul ContaLearn.">
        <PremiumSteps current={2} />
        <div className="pm-grid-2 pm-grid-2--wide">
          <div className="pm-grid-2-main">
            <section className="pm-card pm-panel" aria-labelledby="pm-sub-title">
              <h2 id="pm-sub-title" className="pm-co-title">Planul tau</h2>
              <dl className="pm-kv">
                <div className="pm-kv-row">
                  <dt>Plan</dt>
                  <dd>
                    <FontAwesomeIcon icon={faCrown} className="pm-kv-icon" aria-hidden="true" /> ContaLearn Premium
                  </dd>
                </div>
                <div className="pm-kv-row">
                  <dt>Stare</dt>
                  <dd>
                    <span className="pm-status-pill">Activ</span>
                  </dd>
                </div>
                <div className="pm-kv-row">
                  <dt>Activ pana la</dt>
                  <dd>
                    <FontAwesomeIcon icon={faCalendarCheck} className="pm-kv-icon" aria-hidden="true" />{' '}
                    {formatRoDate(user.premium_expires_at)}
                  </dd>
                </div>
                <div className="pm-kv-row">
                  <dt>Reinnoire</dt>
                  <dd>Manuala — fara plati automate</dd>
                </div>
              </dl>
              <p className="pm-note pm-note--left">
                Daca prelungesti inainte de expirare, luna noua se adauga la perioada ramasa.
              </p>
            </section>
            <BenefitsPanel plan={plan} title="Ce ai inclus" />
          </div>

          <aside className="pm-grid-2-aside">
            <section className="pm-card pm-center">
              <div className="pm-success-badge">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <span className="pm-plan-badge">
                <FontAwesomeIcon icon={faCrown} /> ContaLearn Premium
              </span>
              <p className="pm-note" style={{ marginTop: 14 }}>
                Premium activ pana la <strong>{formatRoDate(user.premium_expires_at)}</strong>.
              </p>
              <div className="pm-actions">
                <button type="button" className="pm-btn pm-btn-teal pm-btn-block" onClick={() => navigate('/functionalpagelogin')}>
                  Inapoi la cursuri
                </button>
                <button type="button" className="pm-btn pm-btn-block" onClick={() => navigate('/premium/checkout')} disabled={!plan}>
                  Prelungeste cu o luna{plan ? ` · ${price}` : ''}
                </button>
              </div>
            </section>
          </aside>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title="Alege planul Premium" subtitle="Un singur plan, tot conținutul. Fără angajament pe termen lung.">
      <PremiumSteps current={0} />
      <div className="pm-grid-2 pm-grid-2--wide">
        <div className="pm-grid-2-main">
          <BenefitsPanel plan={plan} title="Ce primești cu Premium" />
        </div>

        <aside className="pm-grid-2-aside">
          <section className="pm-card" aria-busy={!plan && !error}>
            <span className="pm-plan-badge">
              <FontAwesomeIcon icon={faCrown} /> Recomandat
            </span>
            <h2 className="pm-plan-name">{plan?.name || 'ContaLearn Premium'}</h2>
            <div className="pm-price">
              <span className="pm-price-amount">{price}</span>
              <span className="pm-price-period">/ lună</span>
            </div>
            <ul className="pm-benefits">
              {(plan?.benefits || []).map((benefit) => (
                <li key={benefit} className="pm-benefit">
                  <FontAwesomeIcon icon={faCheck} className="pm-benefit-icon" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            {error && <p className="pm-error">{error}</p>}
            <div className="pm-actions">
              <button
                type="button"
                className="pm-btn pm-btn-primary pm-btn-block"
                disabled={!plan}
                onClick={() => navigate('/premium/checkout')}
              >
                Cumpără Premium
              </button>
            </div>
            <p className="pm-note" style={{ marginTop: 12 }}>
              Acces pentru o lună. Fără reînnoire automată.
            </p>
          </section>
        </aside>
      </div>
    </PremiumLayout>
  );
};

export default PremiumPricing;
